/**
 * Offres — vue d'ensemble des packs BCL Telecom & Énergie.
 * 4 packs (Sérénité Fibre, Sérénité Premium, Essentiel Fibre, Confort Fibre)
 * regroupés par audience (particuliers / professionnels & promoteurs).
 * Reproduit la structure du ServicePlans Loveable + FAQ tarifaire.
 */
import type { PackCode } from '@bcl/shared';
import {
  Building2,
  Check,
  CheckCircle2,
  Home,
  ShieldCheck,
  Sparkles,
  Star,
  Users,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MarketingLayout } from '@/components/site/marketing-layout';

type Pack = {
  id: string;
  /** Code pack côté back (subset des PackCode partagés). */
  code: PackCode;
  name: string;
  price: number;
  description: string;
  features: string[];
  highlight?: boolean;
};

const particularPacks: Pack[] = [
  {
    id: 'serenite',
    code: 'serenite_fibre',
    name: 'Sérénité Fibre',
    price: 340,
    description: 'Solution complète pour maison individuelle',
    features: [
      'Accompagnement dans les démarches administratives',
      'Dossier de raccordement auprès de l\'opérateur',
      'Suivi du dossier en temps réel',
      'Conseil sur les travaux à prévoir',
    ],
  },
  {
    id: 'serenite-premium',
    code: 'serenite_fibre',
    name: 'Sérénité Premium',
    price: 490,
    description: 'Solution premium pour maison individuelle',
    features: [
      'Tous les avantages de Sérénité Fibre',
      'Visite technique sur site',
      'Accompagnement renforcé',
      'Suivi prioritaire du dossier',
      'Conseils personnalisés',
    ],
    highlight: true,
  },
];

const professionalPacks: Pack[] = [
  {
    id: 'essentiel',
    code: 'essentiel_fibre',
    name: 'Essentiel Fibre',
    price: 529,
    description: 'Solution essentielle pour projet collectif',
    features: [
      'Étude technique préliminaire',
      'Montage complet du dossier',
      'Accompagnement avec les opérateurs',
      'Conseils sur l\'infrastructure',
      'Un interlocuteur dédié',
    ],
  },
  {
    id: 'confort',
    code: 'confort_fibre',
    name: 'Confort Fibre',
    price: 799,
    description: 'Solution complète pour projet collectif',
    features: [
      'Tous les avantages de Essentiel Fibre',
      'Étude technique approfondie',
      'Visite sur site',
      'Accompagnement premium',
      'Suivi prioritaire',
      'Conseils personnalisés sur le génie civil',
    ],
    highlight: true,
  },
];

const guarantees = [
  {
    icon: ShieldCheck,
    title: 'Référencés ARCEP',
    description: 'Habilités à monter les dossiers de raccordement collectif.',
  },
  {
    icon: CheckCircle2,
    title: 'Sans surprise',
    description: 'Tarif public, devis écrit, validation avant tout travaux.',
  },
  {
    icon: Sparkles,
    title: 'Interlocuteur dédié',
    description: 'Un référent unique du dépôt au PV de réception.',
  },
];

const comparison = [
  { label: 'Étude administrative', particulier: true, pro: true },
  { label: 'Montage dossier opérateur', particulier: true, pro: true },
  { label: 'Suivi temps réel', particulier: true, pro: true },
  { label: 'Visite technique sur site', particulier: 'Premium', pro: 'Confort' },
  {
    label: 'Coordination génie civil',
    particulier: false,
    pro: true,
  },
  { label: 'Accord syndic / copropriété', particulier: false, pro: true },
  { label: 'PV de réception fibre', particulier: true, pro: true },
];

const faq = [
  {
    q: 'Puis-je payer en plusieurs fois ?',
    a: 'Oui, jusqu\'à 3 fois sans frais à partir de 400 €. Le calendrier de paiement est validé avant signature.',
  },
  {
    q: 'Que se passe-t-il si la fibre n\'est pas disponible chez moi ?',
    a: 'Nous remboursons intégralement le pack si nous ne pouvons pas activer votre raccordement dans un délai de 6 mois (hors retard imputable à l\'opérateur).',
  },
  {
    q: 'Les frais opérateur sont-ils inclus ?',
    a: 'Non. Les frais de mise en service de votre fournisseur d\'accès (Orange, SFR, Free, Bouygues) sont à régler directement auprès de lui.',
  },
  {
    q: 'Puis-je changer de pack en cours de dossier ?',
    a: 'Oui, sur simple demande à votre interlocuteur. La différence tarifaire est ajustée au prorata des prestations déjà réalisées.',
  },
];

function PackCard({
  pack,
  onChoose,
}: {
  pack: Pack;
  onChoose: (pack: Pack) => void;
}) {
  return (
    <Card
      className={
        pack.highlight
          ? 'border-2 border-bcl-blue shadow-lg'
          : 'border-gray-200'
      }
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>{pack.name}</CardTitle>
            <CardDescription className="mt-2">
              {pack.description}
            </CardDescription>
          </div>
          {pack.highlight && (
            <Badge className="bg-bcl-blue text-white">
              <Star className="mr-1 h-3 w-3" />
              Recommandé
            </Badge>
          )}
        </div>
        <div className="mt-4">
          <span className="text-4xl font-bold">{pack.price}</span>
          <span className="text-lg font-normal text-muted-foreground"> €</span>
          <p className="mt-1 text-xs text-gray-500">TTC, paiement unique</p>
        </div>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2 text-sm">
          {pack.features.map((f) => (
            <li key={f} className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-bcl-teal" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </CardContent>
      <CardFooter>
        <Button
          type="button"
          onClick={() => onChoose(pack)}
          className={
            pack.highlight
              ? 'w-full bg-bcl-blue hover:bg-bcl-blue/90'
              : 'w-full'
          }
          variant={pack.highlight ? 'default' : 'outline'}
        >
          Choisir ce pack
        </Button>
      </CardFooter>
    </Card>
  );
}

const PACK_INTENT_KEY = 'bcl.signup.packIntent';

/** Mémorise un pack pour relire après inscription/login. */
export function rememberPackIntent(code: PackCode): void {
  window.sessionStorage.setItem(PACK_INTENT_KEY, code);
}

export function readPackIntent(): PackCode | null {
  return window.sessionStorage.getItem(PACK_INTENT_KEY) as PackCode | null;
}

export function clearPackIntent(): void {
  window.sessionStorage.removeItem(PACK_INTENT_KEY);
}

export function OffresPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  function handleChoose(pack: Pack): void {
    if (user) {
      navigate(`/dashboard/offres?pack=${pack.code}`);
      return;
    }
    rememberPackIntent(pack.code);
    navigate(`/signup?intent=pack&pack=${pack.code}`);
  }

  return (
    <MarketingLayout>
      {/* HERO */}
      <section className="bg-gradient-to-b from-white to-bcl-grey/40 py-20">
        <div className="container px-4 mx-auto">
          <div className="mx-auto max-w-3xl text-center">
            <div className="inline-block rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-bcl-blue mb-4">
              Nos packs
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-balance">
              Une offre adaptée à chaque projet
            </h1>
            <p className="text-lg text-gray-600">
              Quatre packs forfaitaires pour le raccordement fibre — particuliers
              et professionnels — avec accompagnement administratif et
              technique complet.
            </p>
          </div>
        </div>
      </section>

      {/* PARTICULIERS */}
      <section className="py-16 bg-white">
        <div className="container px-4 mx-auto">
          <div className="mb-10 text-center">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-bcl-blue">
              <Home className="h-3.5 w-3.5" />
              Particuliers
            </div>
            <h2 className="text-3xl font-bold mb-3">Maison individuelle</h2>
            <p className="mx-auto max-w-2xl text-gray-600">
              Pour les particuliers qui construisent ou raccordent une maison
              individuelle à la fibre.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 max-w-4xl mx-auto">
            {particularPacks.map((p) => (
              <PackCard key={p.id} pack={p} onChoose={handleChoose} />
            ))}
          </div>
        </div>
      </section>

      {/* PROS */}
      <section className="py-16 bg-bcl-grey/30">
        <div className="container px-4 mx-auto">
          <div className="mb-10 text-center">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-bcl-blue">
              <Building2 className="h-3.5 w-3.5" />
              Pros & promoteurs
            </div>
            <h2 className="text-3xl font-bold mb-3">Projets collectifs</h2>
            <p className="mx-auto max-w-2xl text-gray-600">
              Pour les promoteurs, aménageurs, syndics et collectivités sur
              programmes neufs ou copropriétés.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 max-w-4xl mx-auto">
            {professionalPacks.map((p) => (
              <PackCard key={p.id} pack={p} onChoose={handleChoose} />
            ))}
          </div>
        </div>
      </section>

      {/* GARANTIES */}
      <section className="py-16 bg-white">
        <div className="container px-4 mx-auto">
          <div className="grid gap-6 md:grid-cols-3 max-w-5xl mx-auto">
            {guarantees.map((g) => (
              <Card key={g.title} className="border-gray-100">
                <CardHeader className="text-center">
                  <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-bcl-blue/10 text-bcl-blue mb-2">
                    <g.icon className="h-6 w-6" />
                  </div>
                  <CardTitle className="text-base">{g.title}</CardTitle>
                  <CardDescription>{g.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARATIF */}
      <section className="py-16 bg-bcl-grey/30">
        <div className="container px-4 mx-auto max-w-4xl">
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-bold mb-3">
              Particuliers vs Professionnels
            </h2>
            <p className="text-gray-600">
              Comparez les prestations couvertes par catégorie de pack.
            </p>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-medium">Prestation</TableHead>
                    <TableHead className="text-center font-medium">
                      <Users className="mx-auto mb-1 h-4 w-4 text-bcl-blue" />
                      Particuliers
                    </TableHead>
                    <TableHead className="text-center font-medium">
                      <Building2 className="mx-auto mb-1 h-4 w-4 text-bcl-blue" />
                      Pros / Promoteurs
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparison.map((row) => (
                    <TableRow key={row.label}>
                      <TableCell>{row.label}</TableCell>
                      <TableCell className="text-center">
                        {row.particulier === true ? (
                          <Check className="mx-auto h-4 w-4 text-bcl-teal" />
                        ) : row.particulier === false ? (
                          <span className="text-gray-300">—</span>
                        ) : (
                          <Badge variant="secondary" className="text-xs">
                            {row.particulier}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {row.pro === true ? (
                          <Check className="mx-auto h-4 w-4 text-bcl-teal" />
                        ) : row.pro === false ? (
                          <span className="text-gray-300">—</span>
                        ) : (
                          <Badge variant="secondary" className="text-xs">
                            {row.pro}
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ TARIFAIRE */}
      <section className="py-16 bg-white">
        <div className="container px-4 mx-auto max-w-3xl">
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-bold mb-3">
              Questions sur nos tarifs
            </h2>
            <p className="text-gray-600">
              Toutes les réponses aux questions tarifaires les plus fréquentes.
            </p>
          </div>
          <Card>
            <CardContent className="px-6 py-2">
              <Accordion type="single" collapsible>
                {faq.map((entry, idx) => (
                  <AccordionItem key={entry.q} value={`faq-${idx}`}>
                    <AccordionTrigger className="text-left">
                      {entry.q}
                    </AccordionTrigger>
                    <AccordionContent>{entry.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-bcl-grey">
        <div className="container px-4 mx-auto max-w-4xl">
          <div className="rounded-2xl bg-bcl-blue p-10 text-white text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-balance">
              Prêt à démarrer votre projet&nbsp;?
            </h2>
            <p className="text-white/85 mb-6 max-w-xl mx-auto">
              Créez votre compte et déclarez votre adresse — nous revenons sous
              24 h ouvrées avec une qualification gratuite et personnalisée.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/signup">
                <Button size="lg" variant="secondary">
                  Créer mon compte
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-transparent border-white text-white hover:bg-white/10"
                >
                  Demander un devis
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
